import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { apiGet, apiPost, apiPut, apiDelete } from "../../services/api";
import type { Department, Location } from "../../types";

export default function DepartmentsTab() {
  const { user } = useAuth();
  const orgId = user?.organizationId;
  const [departments, setDepartments] = useState<Department[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState({ name: "", description: "", locationId: "" });

  useEffect(() => { if (orgId) loadData(); }, [orgId]);

  async function loadData() {
    try {
      const [depts, locs] = await Promise.all([
        apiGet<Department[]>(`/organizations/${orgId}/departments`),
        apiGet<Location[]>(`/organizations/${orgId}/locations`),
      ]);
      setDepartments(depts);
      setLocations(locs);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }

  const openCreate = () => { setEditing(null); setForm({ name: "", description: "", locationId: "" }); setShowModal(true); };
  const openEdit = (dept: any) => {
    setEditing(dept.departmentId);
    setForm({ name: dept.name || "", description: dept.description || "", locationId: dept.locationId || "" });
    setShowModal(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editing) {
      await apiPut(`/organizations/${orgId}/departments/${editing}`, form);
    } else {
      await apiPost(`/organizations/${orgId}/departments`, form);
    }
    setShowModal(false);
    await loadData();
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this department?")) return;
    await apiDelete(`/organizations/${orgId}/departments/${id}`);
    await loadData();
  };

  const getLocName = (id?: string) => locations.find((l) => l.locationId === id)?.name || "Org-wide";

  if (loading) return <div className="empty-state">Loading departments...</div>;

  return (
    <div>
      <div className="org-mgmt-header">
        <h2>Departments</h2>
        <button className="btn btn-primary" style={{ width: "auto" }} onClick={openCreate}>+ Add Department</button>
      </div>

      {departments.length === 0 ? (
        <div className="empty-state"><p>No departments yet.</p></div>
      ) : (
        <table className="data-table">
          <thead><tr><th>Name</th><th>Description</th><th>Location</th><th>Status</th><th>Actions</th></tr></thead>
          <tbody>
            {departments.map((dept: any) => (
              <tr key={dept.departmentId}>
                <td style={{ fontWeight: 500 }}>{dept.name}</td>
                <td style={{ color: "#6b7280" }}>{dept.description || "—"}</td>
                <td>{getLocName(dept.locationId)}</td>
                <td><span className={`status-badge ${dept.status}`}>{dept.status}</span></td>
                <td><div className="table-actions"><button onClick={() => openEdit(dept)}>Edit</button><button className="delete" onClick={() => handleDelete(dept.departmentId)}>Delete</button></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "Edit Department" : "Add Department"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="settings-form">
                <div className="form-group"><label>Department Name</label><input name="name" value={form.name} onChange={handleChange} required /></div>
                <div className="form-group"><label>Description</label><input name="description" value={form.description} onChange={handleChange} /></div>
                <div className="form-group"><label>Location</label><select name="locationId" value={form.locationId} onChange={handleChange}><option value="">Org-wide</option>{locations.map((l: any) => (<option key={l.locationId} value={l.locationId}>{l.name}</option>))}</select></div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" style={{ width: "auto" }}>{editing ? "Save Changes" : "Create"}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
